import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface LocaleEntry {
  code: string
  flag: string
  name: string
}

export const LOCALES: LocaleEntry[] = [
  { code: 'en', flag: '🇬🇧', name: 'English' },
  { code: 'pt', flag: '🇵🇹', name: 'Português' },
  { code: 'es', flag: '🇪🇸', name: 'Español' },
  { code: 'ru', flag: '🇷🇺', name: 'Русский' },
  { code: 'fr', flag: '🇫🇷', name: 'Français' },
  { code: 'ka', flag: '🇬🇪', name: 'ქართული' },
  { code: 'zh', flag: '🇨🇳', name: '中文' },
  { code: 'id', flag: '🇮🇩', name: 'Indonesia' },
  { code: 'ro', flag: '🇷🇴', name: 'Română' },
  { code: 'my', flag: '🇲🇲', name: 'မြန်မာဘာသာ' },
  { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
  { code: 'pt-BR', flag: '🇧🇷', name: 'Português (Brasil)' },
  { code: 'vi', flag: '🇻🇳', name: 'Tiếng Việt' },
  { code: 'it', flag: '🇮🇹', name: 'Italiano' },
  { code: 'th', flag: '🇹🇭', name: 'ภาษาไทย' },
  { code: 'no', flag: '🇳🇴', name: 'Norsk' },
]

/**
 * Language selector icon button + dropdown. Shared between the authenticated
 * header (TopBar) and the unauthenticated Login screen so both surfaces use
 * the same underlying i18n hook/state and icon-button treatment.
 */
export function LanguageSelector() {
  const { t, i18n } = useTranslation()
  const [showLangDropdown, setShowLangDropdown] = useState(false)
  const langRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!showLangDropdown) return
    function handleClick(e: MouseEvent) {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setShowLangDropdown(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showLangDropdown])

  const currentLocaleEntry = LOCALES.find(l => l.code === i18n.language) ?? LOCALES[0]!

  return (
    <div className="top-bar-lang" ref={langRef}>
      <button
        type="button"
        className="top-bar-icon-btn"
        aria-label={t('topBar.selectLanguage')}
        title={t('topBar.selectLanguage')}
        aria-haspopup="listbox"
        aria-expanded={showLangDropdown}
        onClick={() => setShowLangDropdown(s => !s)}
      >
        <span style={{ fontSize: 18, lineHeight: 1 }}>{currentLocaleEntry.flag}</span>
      </button>
      {showLangDropdown && (
        <div className="top-bar-lang-menu" role="listbox" aria-label={t('topBar.selectLanguage')}>
          {LOCALES.map(loc => (
            <button
              key={loc.code}
              type="button"
              role="option"
              aria-selected={loc.code === currentLocaleEntry.code}
              className={`top-bar-lang-option${loc.code === currentLocaleEntry.code ? ' active' : ''}`}
              onClick={() => { void i18n.changeLanguage(loc.code); localStorage.setItem('i18n_language', loc.code); setShowLangDropdown(false) }}
            >
              <span style={{ marginRight: 8 }}>{loc.flag}</span>{loc.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
